import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPatch } from '../../services/apiClient';

const AdminPendingProperties = ({ token }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const normalize = (res) => res?.data || res || [];

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [pendingRes, draftRes] = await Promise.all([
        apiGet('/api/admin/properties?status=pending', token),
        apiGet('/api/admin/properties?status=draft', token),
      ]);
      const pendingRows = normalize(pendingRes);
      const draftRows = normalize(draftRes);
      const pendingIds = new Set(pendingRows.map((p) => p.id));
      setRows([...pendingRows, ...draftRows.filter((p) => !pendingIds.has(p.id))]);
    } catch (err) {
      setError(err.message || 'Failed to load pending properties');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    setError('');
    try {
      await apiPatch(`/api/admin/properties/${id}/status`, { status }, token);
      await load();
    } catch (err) {
      setError(err.message || 'Status update failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Properties</p>
          <h1 className="admin-page-title text-xl font-semibold text-slate-900">Pending approval</h1>
        </div>
        <Link to="/admin/properties/new" className="btn btn-primary">
          + Add Property
        </Link>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="bg-white rounded shadow overflow-hidden">
        <table className="table align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th>Title</th>
              <th>City</th>
              <th>Status</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length && (
              <tr>
                <td colSpan={4} className="text-center text-muted py-4">
                  {loading ? 'Loading...' : 'No pending properties'}
                </td>
              </tr>
            )}
            {rows.map((p) => (
              <tr key={p.id}>
                <td className="fw-semibold">
                  <div>{p.title}</div>
                  <div className="text-muted small">{p.slug}</div>
                </td>
                <td>{p.city || '-'}</td>
                <td>
                  <span className="badge bg-warning text-dark text-uppercase">{p.status}</span>
                </td>
                <td className="text-end">
                  <div className="d-flex gap-2 justify-content-end">
                    <Link to={`/admin/properties/${p.id}/edit`} className="btn btn-sm btn-outline-secondary">
                      Review
                    </Link>
                    <button
                      className="btn btn-sm btn-success"
                      disabled={loading}
                      onClick={() => updateStatus(p.id, 'published')}
                    >
                      Approve
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      disabled={loading}
                      onClick={() => updateStatus(p.id, 'unpublished')}
                    >
                      Reject
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPendingProperties;
